import isArray from 'lodash/isArray'
import pluralize from 'pluralize'

import {
    AND_SUFFIX,
    ANY_OF,
    CONTAINS,
    CONTAINS_ALL,
    DOES_NOT_CONTAIN,
    ENDS_WITH,
    EXACT,
    INSENSITIVE_ENDS_WITH,
    INSENSITIVE_EXACT,
    INSENSITIVE_STARTS_WITH,
    NONE_OF,
    STARTS_WITH,
} from 'const/filters'
import {
    BooleanFilter,
    BooleanFilterOption,
    CombinedBooleanFilter,
    SingleClauseFilterValue,
    TypeaheadOption,
} from 'types'

// Order matters here, we want to check _not_icontains before _icontains
const FILTER_OPERATORS = [
    DOES_NOT_CONTAIN,
    CONTAINS,
    CONTAINS_ALL,
    STARTS_WITH,
    ENDS_WITH,
    INSENSITIVE_STARTS_WITH,
    INSENSITIVE_ENDS_WITH,
    EXACT,
    INSENSITIVE_EXACT,
]

const extractOperatorFromKey = (key: any): string | undefined =>
    FILTER_OPERATORS.find((operator) => key.endsWith(operator))

const resolveFilterValue = (
    value: number | string[],
    shouldJoin: boolean
): number | string | string[] => {
    if (shouldJoin && isArray(value)) {
        return value.join()
    }

    return value
}

export function compileBooleanFilter(
    filters: BooleanFilterOption[],
    operatorOptions: BooleanFilterOption[],
    defaultResourceType?: string
): CombinedBooleanFilter {
    const filter = filters[0]

    // Backwards compatibility with the old format
    if (filter.label) {
        const filterVal = filters.map((f) => f.value)
        return {
            [`${defaultResourceType}${AND_SUFFIX}`]: resolveFilterValue(
                filterVal,
                true
            ),
        }
    }

    const filterVal = JSON.parse(filter.value)
    return {
        and: filterVal.and.map((outerItem: any) => ({
            or: outerItem.or.map((innerItem: any) => {
                const operator = operatorOptions.find(
                    (op) => op.value === innerItem.operator
                )
                const filterValue = resolveFilterValue(
                    innerItem.value,
                    !operator?.allowMultiple
                )
                return {
                    [`${innerItem.resourceType}${innerItem.operator}`]:
                        filterValue,
                }
            }),
        })),
    }
}

export const decompileBooleanFilter = (
    filter: CombinedBooleanFilter
): BooleanFilter => {
    if (!filter?.and) {
        return {}
    }
    return {
        and: filter.and.map((outerItem: any) => ({
            or: outerItem.or.map((innerItem: any) => {
                const key = Object.keys(innerItem)[0]

                let operator = extractOperatorFromKey(key)
                const resourceType = key.replace(operator ?? '', '')
                let value = isArray(innerItem[key])
                    ? innerItem[key]
                    : [innerItem[key]]

                return {
                    operator: operator,
                    resourceType,
                    value,
                }
            }),
        })),
    }
}

export const stringifyBooleanFilter = (
    filter: BooleanFilter
): { value: string }[] => [{ value: JSON.stringify(filter) }]

export const getSingleClauseFilterValue = (
    filter: SingleClauseFilterValue[],
    excludeUnknownVal: string = 'Unknown'
): SingleClauseFilterValue | null => {
    const { operator, value = [], excludeUnknown } = filter[0] || {}

    if (!operator) {
        return null
    }

    let valuesToSend = [...value]

    if (
        operator === NONE_OF &&
        excludeUnknown &&
        !valuesToSend.includes(excludeUnknownVal)
    ) {
        valuesToSend.push(excludeUnknownVal)
    }

    return { operator, value: valuesToSend }
}

export const getSingleClauseFilterLabel = (
    operator: string,
    currentValue: string | string[],
    resourceName: string,
    options: TypeaheadOption[] = [],
    excludeUnknown: boolean = false
): string => {
    const getLabel = (val: string): string => {
        const found = options.find((opt) => opt.value === val)
        return found ? found.label : val
    }

    if (
        operator === NONE_OF &&
        excludeUnknown &&
        (!currentValue ||
            (Array.isArray(currentValue) && currentValue.length === 0))
    ) {
        return `${resourceName}: is not Unknown`
    }

    // --- "is not" ---
    if (operator === NONE_OF) {
        if (Array.isArray(currentValue)) {
            if (currentValue.length === 1) {
                const label = getLabel(currentValue[0])
                return `${resourceName}: is not ${label}`
            }

            const displayCount =
                excludeUnknown && currentValue.includes('Unknown')
                    ? currentValue.length - 1
                    : currentValue.length

            return `${resourceName}: is not ${displayCount} ${pluralize(resourceName)}`
        }
        return `${resourceName}: is not ${getLabel(currentValue)}`
    }

    // --- "is any of" ---
    if (operator === ANY_OF) {
        if (Array.isArray(currentValue)) {
            if (currentValue.length === 1) {
                return `${resourceName}: ${getLabel(currentValue[0])}`
            }
            return `${resourceName}: ${currentValue.length}`
        }
        return `${resourceName}: ${getLabel(currentValue)}`
    }

    return resourceName
}
